import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import BackButton from "@/components/BackButton";
import { Users, AlertCircle, Waves, ArrowRight, CheckCircle2, Loader2, X } from "lucide-react";
import { getRelayAgeGroup } from "@/lib/utils";
import { Flag } from "@/lib/countries";
import { payWithRazorpay } from "@/lib/razorpay";

const RELAY_EVENTS = [
  "4x50m Freestyle Relay",
  "4x50m Medley Relay",
  "Mixed 4x50m Freestyle Relay",
  "Mixed 4x50m Medley Relay"
];

export default function RelayRegistration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState("");
  
  // Track the raw input text for each of the 4 inputs
  const [inputs, setInputs] = useState(["", "", "", ""]);
  // Track the resolved athlete objects (or null if not yet resolved)
  const [swimmers, setSwimmers] = useState([null, null, null, null]);
  const [loadingIndexes, setLoadingIndexes] = useState([false, false, false, false]);
  const [strokes, setStrokes] = useState(["", "", "", ""]);
  
  const STROKES = ["Backstroke", "Breaststroke", "Butterfly", "Freestyle"];
  const isMedley = selectedEvent.includes("Medley");
  
  const [candidates, setCandidates] = useState({});  // slot index -> athletes sharing the typed card ID
  const [openEvents, setOpenEvents] = useState([]);
  
  const [submitting, setSubmitting] = useState(false);
  const debounceTimers = useRef([null, null, null, null]);

  // Fetch open relay events on mount
  useEffect(() => {
    api.get("/relay-events")
      .then(res => {
        const status = res.data || {};
        const active = RELAY_EVENTS.filter(ev => status[ev] === true);
        setOpenEvents(active);
      })
      .catch(err => console.error("Failed to load relay events status", err));
  }, []);

  // Pre-fill the logged-in user in the first slot
  useEffect(() => {
    if (user?.user_id) {
      const loadSelf = async () => {
        try {
          const { data } = await api.get(`/athlete-lookup/${encodeURIComponent(user.user_id)}`);
          
          // Format as official ID Card number
          const natPrefix = (user.nationality || "IND").slice(0, 3).toUpperCase();
          const uidSuffix = user.user_id.slice(-4).toUpperCase();
          const formattedId = `ASM26-${natPrefix}-${uidSuffix}`;
          // data.user_id stays the real ID: the card ID is only the last 4 characters and can be shared by two athletes
          
          setInputs(prev => {
            const next = [...prev];
            next[0] = formattedId;
            return next;
          });
          
          setSwimmers(prev => {
            const next = [...prev];
            next[0] = data;
            return next;
          });
        } catch (err) {
          console.error("Failed to load self for relay", err);
        }
      };
      loadSelf();
    }
  }, [user]);

  const lookupAthlete = async (index, userId) => {
    if (!userId.trim()) return;

    // Same ID typed in another slot
    if (inputs.some((t, i) => i !== index && swimmers[i] && t.trim().toLowerCase() === userId.trim().toLowerCase())) {
      return;
    }

    const newLoading = [...loadingIndexes];
    newLoading[index] = true;
    setLoadingIndexes(newLoading);

    try {
      const { data } = await api.get(`/athlete-lookup/${encodeURIComponent(userId)}`);
      // Same person already in another slot (e.g. picked via full ID vs card ID)
      if (swimmers.some((s, i) => i !== index && s?.user_id === data.user_id)) {
        toast.error(`${data.full_name} is already on this team`);
        setSwimmers(prev => { const next = [...prev]; next[index] = null; return next; });
        return;
      }

      // Nationality Check
      if (swimmers[0] && index !== 0 && data.nationality !== swimmers[0].nationality) {
        toast.error(`Athlete must be from ${swimmers[0].nationality} to join this relay`);
        setSwimmers(prev => {
          const next = [...prev];
          next[index] = null;
          return next;
        });
        return;
      }
      
      setSwimmers(prev => {
        const next = [...prev];
        next[index] = data;
        return next;
      });
    } catch (err) {
      // 404 = plain "not found" (slot just clears); 409 = two athletes share this card ID -> let the captain pick
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.candidates) {
        setCandidates(prev => ({ ...prev, [index]: detail.candidates }));
      } else if (err.response?.status === 400) {
        toast.error(typeof detail === "string" ? detail : "Invalid athlete ID");
      }
      setSwimmers(prev => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
    } finally {
      setLoadingIndexes(prev => {
        const next = [...prev];
        next[index] = false;
        return next;
      });
    }
  };

  const handleInputChange = (index, value) => {
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
    setCandidates(prev => ({ ...prev, [index]: null }));
    
    // Clear the resolved athlete if they change the input text
    if (swimmers[index]) {
      setSwimmers(prev => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
    }

    // Clear existing timer
    if (debounceTimers.current[index]) {
      clearTimeout(debounceTimers.current[index]);
    }

    // Auto-lookup after typing stops for 500ms
    if (value.trim().length >= 4) {
      debounceTimers.current[index] = setTimeout(() => {
        lookupAthlete(index, value.trim());
      }, 500);
    }
  };

  const clearSwimmer = (index) => {
    const newInputs = [...inputs];
    newInputs[index] = "";
    setInputs(newInputs);

    const newSwimmers = [...swimmers];
    newSwimmers[index] = null;
    setSwimmers(newSwimmers);
  };

  const allSwimmersResolved = swimmers.every(s => s !== null);
  const totalAge = allSwimmersResolved ? swimmers.reduce((sum, s) => sum + (s.age || 0), 0) : 0;
  const calculatedGroup = allSwimmersResolved ? getRelayAgeGroup(swimmers.map(s => s.age)) : "---";

  const submit = async () => {
    if (!selectedEvent) {
      toast.error("Please select a relay event");
      return;
    }
    if (!allSwimmersResolved) {
      toast.error("Please ensure all 4 athletes are correctly identified");
      return;
    }
    
    if (isMedley && strokes.some(s => !s)) {
      toast.error("Please assign a unique stroke to each swimmer for the medley relay");
      return;
    }
    
    // Nationality Check (Redundant but safe)
    if (!swimmers.every(s => s.nationality === swimmers[0].nationality)) {
      toast.error("All relay members must be of the same nationality");
      return;
    }

    // Additional validation for Mixed Relays
    if (selectedEvent.startsWith("Mixed")) {
      const males = swimmers.filter(s => (s.gender || "").toLowerCase() === "male").length;
      const females = swimmers.filter(s => (s.gender || "").toLowerCase() === "female").length;
      if (males !== 2 || females !== 2) {
        toast.error("Mixed relays must have exactly 2 Male and 2 Female swimmers.");
        return;
      }
    } else {
      // Validation for non-mixed relays
      const genders = new Set(swimmers.map(s => (s.gender || "").toLowerCase()));
      if (genders.size > 1) {
        toast.error("All relay members must be of the same gender for this event.");
        return;
      }
    }

    setSubmitting(true);
    try {
      let finalSwimmers = swimmers;
      if (isMedley) {
        const strokeOrder = ["Backstroke", "Breaststroke", "Butterfly", "Freestyle"];
        const mapped = swimmers.map((s, i) => ({ s, stroke: strokes[i] }));
        mapped.sort((a, b) => strokeOrder.indexOf(a.stroke) - strokeOrder.indexOf(b.stroke));
        finalSwimmers = mapped.map(x => x.s);
      }

      const { data } = await api.post("/relays", {
        event: selectedEvent,
        swimmer_ids: finalSwimmers.map(s => s.user_id)
      });
      
      try {
        const verified = await payWithRazorpay({ relay_team_id: data.team_id }, user);
        if (!verified) {
          toast.info("Payment cancelled. You can complete it later from your dashboard.");
          return;
        }
      } catch (payErr) {
        toast.error(payErr.response?.data?.detail || payErr.message || "Payment failed");
        return;
      }
      
      toast.success(`Relay team registered for ${selectedEvent} in Age Group ${calculatedGroup}!`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to register relay team");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="eyebrow mb-3">Relays</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl mb-2">
          Register a <span className="text-[#00F0FF]">Relay Team</span>
        </h1>
        <p className="text-[#94A3B8] mb-8">
          You are the team captain. Add your 3 teammates by their ID to calculate your FINA age group and register. All members must be from the same country.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="card-dark p-6 border-[#2A3A5F]">
              <h2 className="text-xl font-bold mb-4 text-white">1. Select Event</h2>
              <div className="space-y-3">
                {openEvents.length === 0 ? (
                  <div className="p-4 text-center text-[#94A3B8] border border-[#2A3A5F] rounded-lg bg-[#142040]/50">
                    No relay events are currently open for registration.
                  </div>
                ) : (
                  openEvents.map(ev => (
                    <button
                      key={ev}
                      onClick={() => {
                        setSelectedEvent(ev);
                        setStrokes(["", "", "", ""]);
                      }}
                      className={`w-full text-left p-4 rounded-lg border transition-all flex items-center gap-3 ${
                        selectedEvent === ev 
                          ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" 
                          : "border-[#2A3A5F] bg-[#142040] text-[#94A3B8] hover:border-[#00F0FF]/40 hover:text-white"
                      }`}
                    >
                      <Waves className={`w-5 h-5 ${selectedEvent === ev ? "text-[#00F0FF]" : "text-[#64748B]"}`} />
                      <span className="font-semibold">{ev}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="card-dark p-6 border-[#2A3A5F]">
              <h2 className="text-xl font-bold mb-4 text-white flex items-center justify-between">
                <span>2. Team Members</span>
                <Users className="w-5 h-5 text-[#00F0FF]" />
              </h2>
              <p className="text-xs text-[#94A3B8] mb-4">
                Type your teammate's ID to automatically find them.
              </p>
              
              <div className="space-y-4">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="flex items-start gap-4">
                    <div className={`w-8 h-8 mt-2 rounded-full border flex items-center justify-center text-sm font-bold shrink-0 ${
                      i === 0 ? "bg-[#00F0FF]/20 border-[#00F0FF] text-[#00F0FF]" : "bg-[#142040] border-[#2A3A5F] text-[#00F0FF]"
                    }`}>
                      {i === 0 ? "C" : i + 1}
                    </div>
                    
                    <div className="flex-1 space-y-2 relative">
                      {/* We keep the input rendered always so they can edit it, but style it differently if resolved */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={i === 0 ? "Your ID" : `Teammate ${i} ID`}
                          value={inputs[i]}
                          onChange={e => handleInputChange(i, e.target.value)}
                          disabled={i === 0} // Captain slot is locked
                          className={`input-dark w-full uppercase pr-10 ${i === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {loadingIndexes[i] ? (
                            <Loader2 className="w-4 h-4 text-[#00F0FF] animate-spin" />
                          ) : swimmers[i] ? (
                            <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                          ) : inputs[i].length > 3 ? (
                            <X className="w-4 h-4 text-[#FF3B30]" />
                          ) : null}
                        </div>
                      </div>

                      {swimmers[i] && (
                        <div className={`px-3 py-2 rounded-lg flex items-center justify-between ${
                          i === 0 ? "bg-[#00F0FF]/[0.08] border border-[#00F0FF]/30" : "bg-[#10B981]/[0.08] border border-[#10B981]/30"
                        }`}>
                          <div>
                            <div className="text-sm font-bold text-white flex items-center gap-1.5">
                              {swimmers[i].full_name} 
                              {swimmers[i].nationality && <Flag country={swimmers[i].nationality} size={14} />}
                              {i === 0 && <span className="text-[10px] uppercase tracking-wider text-[#00F0FF] px-1.5 py-0.5 bg-[#00F0FF]/10 rounded-full ml-1">Captain</span>}
                            </div>
                            <div className="text-xs text-[#94A3B8] mt-0.5">
                              Age: {swimmers[i].age} · {swimmers[i].gender}
                            </div>
                          </div>
                          {i !== 0 && (
                            <button onClick={() => clearSwimmer(i)} aria-label={`Remove ${swimmers[i].full_name}`} className="w-11 h-11 -mr-2 flex items-center justify-center text-[#94A3B8] hover:text-[#FF3B30] transition-colors rounded-md hover:bg-white/5">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                      
                      {candidates[i] && !swimmers[i] && (
                        <div className="rounded-lg border border-[#FBBF24]/40 bg-[#FBBF24]/[0.06] p-3 space-y-2" role="group" aria-label="Choose the right athlete">
                          <p className="text-xs text-[#FBBF24] font-semibold">More than one athlete has this ID. Pick the right person:</p>
                          {candidates[i].map(c => (
                            <button
                              key={c.user_id}
                              type="button"
                              onClick={() => { setCandidates(prev => ({ ...prev, [i]: null })); lookupAthlete(i, c.user_id); }}
                              className="w-full text-left px-3 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.09] border border-white/[0.08] text-sm text-white cursor-pointer"
                            >
                              {c.full_name} <span className="text-xs text-[#94A3B8]">· {c.gender}{c.age != null ? ` · Age ${c.age}` : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {!swimmers[i] && !candidates[i] && inputs[i].length > 4 && !loadingIndexes[i] && (
                        <div className="text-xs text-[#FF3B30] px-1">
                          Athlete not found. Check the ID.
                        </div>
                      )}
                      
                      {isMedley && swimmers[i] && (
                        <div className="mt-2">
                          <select
                            value={strokes[i]}
                            onChange={e => {
                              const newStrokes = [...strokes];
                              newStrokes[i] = e.target.value;
                              setStrokes(newStrokes);
                            }}
                            className="input-dark w-full text-sm py-2 px-3 appearance-none"
                            style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2300F0FF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right .7em top 50%", backgroundSize: ".65em auto" }}
                          >
                            <option value="">Select Stroke</option>
                            {STROKES.map(stroke => (
                              <option 
                                key={stroke} 
                                value={stroke} 
                                disabled={strokes.includes(stroke) && strokes[i] !== stroke}
                              >
                                {stroke}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-dark p-6 border-[#00F0FF]/30 bg-[#00F0FF]/[0.02] sticky top-24">
              <div className="eyebrow mb-3">Team Summary</div>
              
              <div className="mb-6">
                <div className="text-sm text-[#94A3B8] mb-1">Selected Event</div>
                <div className="font-bold text-lg text-white">
                  {selectedEvent || "No event selected"}
                </div>
              </div>

              <div className="divider-line my-4" />

              <div className="mb-6">
                <div className="text-sm text-[#94A3B8] mb-1">Calculated Age Group</div>
                <div className="text-4xl font-display font-black text-[#00F0FF]">
                  {calculatedGroup}
                </div>
                {allSwimmersResolved && (
                  <div className="text-xs mt-2 text-[#64748B]">
                    Sum of ages: {totalAge}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#00F0FF]/[0.08] border border-[#00F0FF]/20 mb-6">
                <AlertCircle className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                <p className="text-xs text-[#CBD5E1]">
                  By clicking Register Team, you confirm that all entered athletes have agreed to be part of this relay.
                </p>
              </div>

              <button
                disabled={submitting || !selectedEvent || !allSwimmersResolved}
                onClick={submit}
                className="btn-primary w-full justify-center disabled:opacity-50"
              >
                {submitting ? "Processing..." : "Register Team"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
